import { useEffect, useMemo, useState } from "react";
import useRememberTable from "src/hooks/useRememberTable";
import {
  GetReportDataResponse,
  useLazyGetReportDataQuery,
} from "src/services/restApi/queries/report.service";
import { format } from "date-fns";
import { Box } from "@mui/material";
import TableFilter from "src/components/tableFilter/TableFilter";
import { useAppZStore } from "src/store/zStore";
import { BaseDrawer } from "src/components/dialogs/BaseDrawer";
import {
  defaultGrouping,
  DEPOSITS_FILTER_CONFIG,
  TRADES_FILTER_CONFIG,
  WITHDRAWALS_FILTER_CONFIG,
} from "../utils/consts";
import {
  ReportFilter,
  Grouping,
  AggregatedTradeData,
  AggregatedDepositData,
  AggregatedWithdrawalData,
} from "../types";
import { createTradesCsv, aggregateTradeData } from "../utils/tradeUtils";
import TableControls from "./TableControls";
import GroupByControls from "./GroupByControls";
import TradesTable from "./TradesTable";
import { ReportTypes } from "../ReportsDashboard";
import { aggregateDepositData, createDepositsCsv } from "../utils/depositUtils";
import DepositsTable from "./DepositsTable";
import {
  aggregateWithdrawalData,
  createWithdrawalsCsv,
} from "../utils/withdrawalUtils";
import WithdrawalsTable from "./WithdrawalsTable";

const ReportsTable = ({
  reportType,
  data,
  isLoading,
  isFetching,
  limit,
  setLimit,
}: {
  reportType: ReportTypes;
  data: GetReportDataResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  limit: number;
  setLimit: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const tableName = `Pulse ${reportType} Reports`;

  const [groupings, setGroupings] = useState<Grouping[]>([]);
  const openDrawer = useAppZStore((state) => state.openDrawer);
  const filtersLength = useAppZStore((state) =>
    state.filters[tableName]
      ? Object.keys(state.filters[tableName]).length
      : null
  );

  // Helper to update a grouping
  const updateGrouping = (idx: number, changes: Partial<Grouping>) => {
    setGroupings((prev) =>
      prev.map((g, i) => (i === idx ? { ...g, ...changes } : g))
    );
  };

  // Helper to add a new grouping
  const addGrouping = () => {
    setGroupings((prev) => [...prev, { ...defaultGrouping }]);
  };

  // Helper to remove a grouping
  const removeGrouping = (idx: number) => {
    setGroupings((prev) => prev.filter((_, i) => i !== idx));
  };

  const aggregatedData = useMemo(() => {
    if (!data?.data) return [];

    switch (reportType) {
      case ReportTypes.Trades:
        return aggregateTradeData(groupings, data.data);
      case ReportTypes.Deposits:
        return aggregateDepositData(groupings, data.data);
      case ReportTypes.Withdrawals:
        return aggregateWithdrawalData(groupings, data.data);
      default:
        console.error("Unsupported report type for aggregation");
        return [];
    }
  }, [data?.data, groupings, reportType]);

  const handleDownloadCSV = () => {
    if (!aggregatedData.length || !data?.data) return;

    let csvContent = "";
    switch (reportType) {
      case ReportTypes.Trades:
        csvContent = createTradesCsv(
          groupings,
          data,
          aggregatedData as AggregatedTradeData[]
        );
        break;
      case ReportTypes.Deposits:
        csvContent = createDepositsCsv(
          groupings,
          data,
          aggregatedData as AggregatedDepositData[]
        );
        break;
      case ReportTypes.Withdrawals:
        csvContent = createWithdrawalsCsv(
          groupings,
          data,
          aggregatedData as AggregatedWithdrawalData[]
        );
        break;
      default:
        console.error("Unsupported report type for CSV download");
        return;
    }

    // Create and trigger download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `${reportType}_report_${format(new Date(), "yyyy-MM-dd")}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openFilter = () => {
    let filterConfig = TRADES_FILTER_CONFIG;

    switch (reportType) {
      case ReportTypes.Trades:
        filterConfig = TRADES_FILTER_CONFIG;
        break;
      case ReportTypes.Deposits:
        filterConfig = DEPOSITS_FILTER_CONFIG;
        break;
      case ReportTypes.Withdrawals:
        filterConfig = WITHDRAWALS_FILTER_CONFIG;
        break;
      default:
        console.error("Unsupported report type for filters");
        return;
    }

    openDrawer({
      drawerContent: (
        <TableFilter tableName={tableName} config={filterConfig} />
      ),
      drawerDirection: "right",
    });
  };

  const table = () => {
    switch (reportType) {
      case ReportTypes.Trades:
        return (
          <TradesTable
            groupings={groupings}
            isLoading={isLoading}
            isFetching={isFetching}
            aggregatedTradeData={aggregatedData as AggregatedTradeData[]}
            rawData={data?.data}
          />
        );
      case ReportTypes.Deposits:
        return (
          <DepositsTable
            groupings={groupings}
            isLoading={isLoading}
            isFetching={isFetching}
            aggregatedDepositData={aggregatedData as AggregatedDepositData[]}
            rawData={data?.data}
          />
        );
      case ReportTypes.Withdrawals:
        return (
          <WithdrawalsTable
            groupings={groupings}
            isLoading={isLoading}
            isFetching={isFetching}
            aggregatedWithdrawalData={
              aggregatedData as AggregatedWithdrawalData[]
            }
            rawData={data?.data}
          />
        );
      default:
        return <div>Unsupported report type</div>;
    }
  };

  return (
    <Box>
      {/* Download CSV and grouping controls below */}
      <TableControls
        filtersLength={filtersLength}
        openFilter={openFilter}
        groupings={groupings}
        addGrouping={addGrouping}
        handleDownloadCSV={handleDownloadCSV}
        isLoading={isLoading}
        isFetching={isFetching}
        data={aggregatedData}
        limit={limit}
        setLimit={setLimit}
      />
      <GroupByControls
        groupings={groupings}
        data={data?.data}
        addGrouping={addGrouping}
        updateGrouping={updateGrouping}
        removeGrouping={removeGrouping}
        reportType={reportType}
      />
      {table()}
      <BaseDrawer />
    </Box>
  );
};

export default ReportsTable;
